import React, { useContext, useEffect, useState } from 'react';
import { Field as FormikField, FormikProps } from 'formik';
import { RouterContext } from '../../gs-component-right-rail';
import { FormBlockColumn } from '@xo-union/tk-component-form-grid';
import { AddressDisplay, Typeahead } from '../../gs-component-form';
import { MemberWeddingClient } from '../../wws-api-client';
import { DisplayLink } from '@xo-union/tk-ui-links';
import GeoAPIClient from '@xo-union/sdk-geo';

import RightRailSection from '../components/RightRailSection';
import { LocationContext } from '../components/LocationContext';
import { SoEInitialValues } from './ScheduleOfEventsForm';
import { hasAddress, getLocationVenuesLink } from '../helpers/addressHelpers';
import { GoogleAddressValues } from '../helpers/objectifyGoogleAddressComponents';
import addressComponentParser, { LocalVendorValues } from '../helpers/addressComponentHelper';
import { TrackingContext } from '../components/TrackingContext';
import styles from './venueForm.scss';

interface Location {
  city?: string;
  state?: string;
  stateCode?: string;
}
interface Props {
  formProps: FormikProps<SoEInitialValues>;
  autoFocus?: boolean;
  location?: Location;
}

const defaultLocation = { city: undefined, state: undefined, stateCode: undefined };

export const VenueForm: React.FC<Props> = ({ formProps, autoFocus, location: locationProp = defaultLocation }) => {
  const { city, stateCode } = locationProp;
  const [location, setLocation] = useState<Location>(locationProp);
  const [venuesLink, setVenuesLink] = useState(() => getLocationVenuesLink(city, stateCode));
  const { track } = useContext(TrackingContext);
  const { setFieldValue, values, resetForm } = formProps;
  const { setWeddingAddress } = useContext(LocationContext);
  const memberWeddingApi = new MemberWeddingClient();
  const { push } = useContext(RouterContext);
  const geoClient = new GeoAPIClient();

  useEffect(() => {
    // get location by IP
    const getGeoLocation = async () => {
      const { body } = await geoClient.locationsByClientIP();
      if (body?.locations?.length) {
        const { city, state, stateCode } = body.locations[0];
        setLocation({ city, state, stateCode });
        setVenuesLink(() => getLocationVenuesLink(city, stateCode));
      }
    };

    // get weddimg location from wws api
    const getWeddingLocation = async () => {
      const { data } = await memberWeddingApi.getMemberWeddingLocation();
      if (data?.data?.weddingWebsite?.weddingLocation) {
        const weddingLocation = data.data.weddingWebsite.weddingLocation.split(', ');

        if (weddingLocation.length > 1) {
          setLocation({
            city: weddingLocation[0],
            state: weddingLocation[1],
            stateCode: weddingLocation[1],
          });
          setVenuesLink(() => getLocationVenuesLink(weddingLocation[0], weddingLocation[1]));
        }
      } else {
        // if no wedding location is set, fallback to getting location by IP
        getGeoLocation();
      }
    };

    // get location only if is not passed in props
    if (!city && !stateCode) getWeddingLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (addressComponentValues: GoogleAddressValues | LocalVendorValues, source: string): void => {
    const { vendor_name, gdsAddress, address_components, vendor_profile_id } = addressComponentParser(
      addressComponentValues,
      source
    );
    setFieldValue('location.zip', gdsAddress.zip);
    setFieldValue('location.city', gdsAddress.city);
    setFieldValue('location.state', gdsAddress.state);
    setFieldValue('location.country', gdsAddress.country);
    setFieldValue('location.street1', gdsAddress.street1);
    setFieldValue('location.name', vendor_name);

    setWeddingAddress({ vendor_name, address_components, vendor_profile_id });
  };

  const deleteEventLocation = (): void => {
    resetForm({
      values: {
        ...values,
        // @ts-ignore
        location: {
          zip: '',
          city: '',
          state: '',
          country: '',
          street1: '',
          name: '',
        },
      },
    });
  };

  const editCallback = (): void => push('customVendor');

  return (
    <RightRailSection name="Venue">
      {hasAddress(values.location) ? (
        <FormBlockColumn>
          <AddressDisplay
            vendorType="Venue Name"
            data-testid="venue_name"
            deleteCallback={deleteEventLocation}
            editCallback={editCallback}
            {...values.location}
          />
        </FormBlockColumn>
      ) : (
        <FormBlockColumn className={styles.venueField}>
          <FormikField
            name="location.name"
            label="Venue Name"
            data-testid="venue_name"
            onChange={onChange}
            component={Typeahead}
            onClickAddCustom={editCallback}
            allowCustomVendor
            autoFocus={autoFocus}
          />
        </FormBlockColumn>
      )}

      <FormBlockColumn>
        <DisplayLink
          href={venuesLink}
          target="_blank"
          color="primary"
          size="lg"
          iconName="linkout"
          data-testid="explore_venues"
          onClick={() => {
            track('MP-Venues', { source: 'event modal' });
          }}
          className={styles.linkIcon}
        >
          {location.city && location.stateCode
            ? `Explore venues in ${location.city}, ${location.stateCode}`
            : 'Explore venues in your area'}
        </DisplayLink>
      </FormBlockColumn>
    </RightRailSection>
  );
};
